// League management service
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { v4 as uuidv4 } from 'uuid';

// Create a new league
export const createLeague = async (name, description, adminId, adminName) => {
  const leagueId = uuidv4();
  const leagueRef = doc(db, 'leagues', leagueId);

  const league = {
    name,
    description: description || '',
    adminId,
    adminName,
    members: [{
      odplayerId: adminId,
      displayName: adminName,
      role: 'admin',
      joinedAt: new Date().toISOString()
    }],
    memberIds: [adminId], // For easier querying
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  await setDoc(leagueRef, league);

  return {
    id: leagueId,
    ...league
  };
};

// Get league by ID
export const getLeague = async (leagueId) => {
  const leagueRef = doc(db, 'leagues', leagueId);
  const leagueSnap = await getDoc(leagueRef);

  if (!leagueSnap.exists()) {
    return null;
  }

  return {
    id: leagueSnap.id,
    ...leagueSnap.data()
  };
};

// Get all leagues for a user (where they are a member)
export const getLeaguesForUser = async (userId) => {
  const leaguesRef = collection(db, 'leagues');
  const q = query(leaguesRef, where('memberIds', 'array-contains', userId));
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

// Update league details
export const updateLeague = async (leagueId, updates) => {
  const leagueRef = doc(db, 'leagues', leagueId);
  await updateDoc(leagueRef, {
    ...updates,
    updatedAt: serverTimestamp()
  });
};

// Add member to league
export const addMemberToLeague = async (leagueId, playerId, displayName, role = 'member') => {
  const leagueRef = doc(db, 'leagues', leagueId);

  const newMember = {
    odplayerId: playerId,
    displayName,
    role,
    joinedAt: new Date().toISOString()
  };

  await updateDoc(leagueRef, {
    members: arrayUnion(newMember),
    memberIds: arrayUnion(playerId),
    updatedAt: serverTimestamp()
  });
};

// Remove member from league
export const removeMemberFromLeague = async (leagueId, playerId) => {
  const league = await getLeague(leagueId);
  if (!league) return;

  const memberToRemove = league.members.find(m => m.playerId === playerId);
  if (!memberToRemove) return;

  const leagueRef = doc(db, 'leagues', leagueId);
  await updateDoc(leagueRef, {
    members: arrayRemove(memberToRemove),
    memberIds: arrayRemove(playerId),
    updatedAt: serverTimestamp()
  });
};

// Update member role in league
export const updateMemberRole = async (leagueId, playerId, newRole) => {
  const league = await getLeague(leagueId);
  if (!league) return;

  const updatedMembers = league.members.map(m => {
    if (m.playerId === playerId) {
      return { ...m, role: newRole };
    }
    return m;
  });

  const leagueRef = doc(db, 'leagues', leagueId);
  await updateDoc(leagueRef, {
    members: updatedMembers,
    updatedAt: serverTimestamp()
  });
};

// Check if user is admin of league
export const isLeagueAdmin = async (leagueId, userId) => {
  const league = await getLeague(leagueId);
  if (!league) return false;
  return league.adminId === userId;
};

// Check if user is member of league
export const isLeagueMember = async (leagueId, userId) => {
  const league = await getLeague(leagueId);
  if (!league) return false;
  return league.memberIds.includes(userId);
};

// Delete league (only admin can do this)
export const deleteLeague = async (leagueId) => {
  const leagueRef = doc(db, 'leagues', leagueId);
  await deleteDoc(leagueRef);
};

// Get all competitions for a league
export const getCompetitionsForLeague = async (leagueId) => {
  const competitionsRef = collection(db, 'competitions');
  const q = query(competitionsRef, where('leagueId', '==', leagueId));
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

// League invitation system
export const createLeagueInvitation = async (leagueId, leagueName, invitedByName) => {
  const inviteId = uuidv4();
  const inviteRef = doc(db, 'leagueInvitations', inviteId);

  const invitation = {
    leagueId,
    leagueName,
    invitedByName,
    createdAt: serverTimestamp(),
    expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days
    status: 'active'
  };

  await setDoc(inviteRef, invitation);

  return {
    id: inviteId,
    ...invitation
  };
};

// Get league invitation by ID
export const getLeagueInvitation = async (inviteId) => {
  const inviteRef = doc(db, 'leagueInvitations', inviteId);
  const inviteSnap = await getDoc(inviteRef);

  if (!inviteSnap.exists()) {
    return null;
  }

  return {
    id: inviteSnap.id,
    ...inviteSnap.data()
  };
};

// Accept league invitation
export const acceptLeagueInvitation = async (inviteId, playerId, displayName) => {
  const invitation = await getLeagueInvitation(inviteId);
  if (!invitation) {
    throw new Error('Invitation not found');
  }

  if (invitation.status !== 'active') {
    throw new Error('Invitation is no longer active');
  }

  if (new Date(invitation.expiresAt) < new Date()) {
    throw new Error('Invitation has expired');
  }

  // Add member to league
  await addMemberToLeague(invitation.leagueId, playerId, displayName);

  // Mark invitation as used (optional - could keep for tracking)
  const inviteRef = doc(db, 'leagueInvitations', inviteId);
  await updateDoc(inviteRef, {
    status: 'used',
    usedBy: playerId,
    usedAt: serverTimestamp()
  });

  return invitation.leagueId;
};

// Link an existing competition to a league and add all its players as members
export const linkCompetitionToLeague = async (leagueId, competitionId) => {
  // Get the league
  const league = await getLeague(leagueId);
  if (!league) {
    throw new Error('League not found');
  }

  // Get the competition
  const compRef = doc(db, 'competitions', competitionId);
  const compSnap = await getDoc(compRef);
  if (!compSnap.exists()) {
    throw new Error('Competition not found');
  }

  // Get all players in the competition
  const playersRef = collection(db, 'players');
  const playersQuery = query(playersRef, where('competitionId', '==', competitionId));
  const playersSnap = await getDocs(playersQuery);
  const players = playersSnap.docs.map(d => ({ id: d.id, ...d.data() }));

  // Debug: Log what we found
  console.log('=== Link Competition Debug ===');
  console.log('Competition ID:', competitionId);
  console.log('Players found:', players.length);
  players.forEach(p => {
    console.log(`  - ${p.displayName}: id=${p.id}, odplayerId=${p.odplayerId || 'NOT SET'}`);
  });
  console.log('Existing league memberIds:', league.memberIds);

  // Build updated members list (avoid duplicates)
  const existingMemberIds = new Set(league.memberIds || []);
  const newMembers = [...(league.members || [])];
  const newMemberIds = [...(league.memberIds || [])];

  let addedCount = 0;
  for (const player of players) {
    // Use odplayerId if available, otherwise fall back to document ID
    const playerId = player.odplayerId || player.id;
    console.log(`Processing ${player.displayName}: playerId=${playerId}, exists=${existingMemberIds.has(playerId)}`);
    if (!playerId) continue;

    if (!existingMemberIds.has(playerId)) {
      console.log(`  -> Adding ${player.displayName} to league`);
      newMembers.push({
        playerId: playerId,
        displayName: player.displayName || 'Unknown',
        role: 'member',
        joinedAt: new Date().toISOString()
      });
      newMemberIds.push(playerId);
      existingMemberIds.add(playerId);
      addedCount++;
    } else {
      console.log(`  -> Skipping ${player.displayName} (already exists)`);
    }
  }
  console.log(`Total added: ${addedCount}, newMembers count: ${newMembers.length}`);

  // Update the league with new members
  const leagueRef = doc(db, 'leagues', leagueId);
  await updateDoc(leagueRef, {
    members: newMembers,
    memberIds: newMemberIds,
    updatedAt: serverTimestamp()
  });

  // Link competition to league
  await updateDoc(compRef, {
    leagueId: leagueId,
    updatedAt: serverTimestamp()
  });

  // === Import games from competition to Game Library ===
  console.log('=== Importing Games to Game Library ===');

  // Get all games from the competition
  const gamesRef = collection(db, 'games');
  const gamesQuery = query(gamesRef, where('competitionId', '==', competitionId));
  const gamesSnap = await getDocs(gamesQuery);
  const games = gamesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  console.log(`Found ${games.length} games in competition`);

  // Get all categories from the competition
  const categoriesRef = collection(db, 'categories');
  const categoriesQuery = query(categoriesRef, where('competitionId', '==', competitionId));
  const categoriesSnap = await getDocs(categoriesQuery);
  const categories = categoriesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  console.log(`Found ${categories.length} categories in competition`);

  // Get existing game sections for this league
  const gameSectionsRef = collection(db, 'gameSections');
  const gameSectionsQuery = query(gameSectionsRef, where('leagueId', '==', leagueId));
  const gameSectionsSnap = await getDocs(gameSectionsQuery);
  const existingGameSections = gameSectionsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

  // Create a map of existing game sections by name+category for duplicate detection
  const existingGameMap = new Map();
  existingGameSections.forEach(gs => {
    const key = `${gs.name.toLowerCase()}|${gs.categoryName?.toLowerCase() || ''}`;
    existingGameMap.set(key, gs);
  });

  // Group games by name and category, calculating totals
  const gameDataMap = new Map();
  games.forEach(game => {
    const category = categories.find(c => c.id === game.categoryId);
    const categoryName = category?.name || game.category || 'Uncategorized';
    const categoryId = category?.id || categoryName.toLowerCase().replace(/\s+/g, '-');

    const gameKey = `${game.name.toLowerCase()}|${categoryName.toLowerCase()}`;

    if (!gameDataMap.has(gameKey)) {
      gameDataMap.set(gameKey, {
        name: game.name,
        categoryId,
        categoryName,
        totalTimesPlayed: 0,
        totalDurationMinutes: 0
      });
    }

    const gameData = gameDataMap.get(gameKey);
    gameData.totalTimesPlayed += 1;

    // Calculate duration from actual start/end times if available
    if (game.actualStartTime && game.actualEndTime) {
      const start = new Date(game.actualStartTime);
      const end = new Date(game.actualEndTime);
      const duration = Math.round((end - start) / 60000);
      if (duration > 0 && duration < 600) { // Reasonable duration (under 10 hours)
        gameData.totalDurationMinutes += duration;
      }
    }
  });

  // Create game sections for new games
  let gamesAdded = 0;
  for (const [gameKey, gameData] of gameDataMap) {
    // Check if already exists
    if (existingGameMap.has(gameKey)) {
      console.log(`  Game "${gameData.name}" (${gameData.categoryName}) already exists, skipping`);
      continue;
    }

    const gameSectionId = uuidv4();
    const averageDuration = gameData.totalTimesPlayed > 0 && gameData.totalDurationMinutes > 0
      ? Math.round(gameData.totalDurationMinutes / gameData.totalTimesPlayed)
      : null;

    const gameSectionDoc = {
      leagueId,
      name: gameData.name,
      categoryId: gameData.categoryId,
      categoryName: gameData.categoryName,
      photoUrl: null,
      rulesUrl: '',
      averageDurationMinutes: averageDuration,
      totalTimesPlayed: gameData.totalTimesPlayed,
      totalDurationMinutes: gameData.totalDurationMinutes,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    await setDoc(doc(db, 'gameSections', gameSectionId), gameSectionDoc);
    gamesAdded++;
    console.log(`  Added game: ${gameData.name} (${gameData.categoryName}) - played ${gameData.totalTimesPlayed}x, avg ${averageDuration || 'N/A'} min`);
  }

  console.log(`Total games added to library: ${gamesAdded}`);

  return {
    playersAdded: addedCount,
    totalMembers: newMemberIds.length,
    gamesAdded: gamesAdded
  };
};

